import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Table, Thead, Tbody, Tr, Th, Td, Button,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  useDisclosure, Flex, Text, Box, HStack, Input,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, useToast,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import axiosInstance from '../../../api/AxiosInstance';
import FormularioPrograma from './FormularioPrograma';

interface Props {
  idPlan: number;
  nombrePlan: string;
}

const TablaProgramasCulturales: React.FC<Props> = ({ idPlan, nombrePlan }) => {
  const toast = useToast();
  const cancelRef = useRef<any>(null);

  const { isOpen: isOpenVer, onOpen: onOpenVer, onClose: onCloseVer } = useDisclosure();
  const { isOpen: isOpenEditar, onOpen: onOpenEditar, onClose: onCloseEditar } = useDisclosure();
  const { isOpen: isOpenNuevo, onOpen: onOpenNuevo, onClose: onCloseNuevo } = useDisclosure();
  const { isOpen: isOpenEliminar, onOpen: onOpenEliminar, onClose: onCloseEliminar } = useDisclosure();

  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [data, setData] = useState<any[]>([]);
  const [cargando, setCargando] = useState(false);
  const [eliminando, setEliminando] = useState(false);
  const [busqueda, setBusqueda] = useState('');

  const fetchData = useCallback(async () => {
    setCargando(true);
    try {
      const res = await axiosInstance.get(`/cultura/programas/plan/${idPlan}`);
      setData(res.data || []);
    } catch {
      setData([]);
    } finally {
      setCargando(false);
    }
  }, [idPlan]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const datosFiltrados = data.filter((item) =>
    busqueda
      ? item.nombrePrograma?.toLowerCase().includes(busqueda.toLowerCase()) ||
        item.comunidad?.toLowerCase().includes(busqueda.toLowerCase()) ||
        item.responsable?.toLowerCase().includes(busqueda.toLowerCase())
      : true
  );

  const handleVer = (item: any) => { setSelectedItem(item); onOpenVer(); };
  const handleEditar = (item: any) => { setSelectedItem(item); onOpenEditar(); };
  const handleEliminarClick = (item: any) => { setSelectedItem(item); onOpenEliminar(); };

  const handleGuardado = () => {
    onCloseNuevo();
    onCloseEditar();
    fetchData();
  };

  const handleEliminarConfirmar = async () => {
    if (!selectedItem) return;
    setEliminando(true);
    try {
      await axiosInstance.delete(`/cultura/programas/${selectedItem.id}`);
      toast({ title: 'Programa eliminado', status: 'success', duration: 3000 });
      onCloseEliminar();
      fetchData();
    } catch (error: any) {
      toast({
        title: 'Error al eliminar',
        description: error?.response?.data?.message || 'No se pudo eliminar el programa.',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setEliminando(false);
    }
  };

  const fmt = (v: string) =>
    v ? new Date(v).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

  const fmtPesos = (v: any) =>
    v != null ? `$${Number(v).toLocaleString('es-MX', { minimumFractionDigits: 2 })}` : '—';

  return (
    <Box>
      <Flex mb={4} justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <Box>
          <Text fontSize="sm" color="gray.500">Plan:</Text>
          <Text fontWeight="semibold" fontSize="sm" color="#5b182b">{nombrePlan}</Text>
        </Box>
        <HStack spacing={3}>
          <Input
            placeholder="Buscar programa, comunidad o responsable..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            size="sm"
            w={{ base: 'full', md: '260px' }}
          />
          <Button
            size="sm"
            leftIcon={<AddIcon />}
            bg="#5b182b"
            color="white"
            _hover={{ bg: '#691f30' }}
            onClick={onOpenNuevo}
          >
            Agregar programa
          </Button>
        </HStack>
      </Flex>

      <Box overflowX="auto" borderRadius="md" boxShadow="md" bg="white">
        <Table variant="simple" size="sm">
          <Thead bg="#5b182b">
            <Tr>
              <Th color="white" fontSize="xs">Nombre del programa</Th>
              <Th color="white" fontSize="xs">Comunidad</Th>
              <Th color="white" fontSize="xs">Tipo</Th>
              <Th color="white" fontSize="xs">Responsable</Th>
              <Th color="white" fontSize="xs">Fecha Inicio</Th>
              <Th color="white" fontSize="xs">Fecha Fin</Th>
              <Th color="white" fontSize="xs" isNumeric>Presupuesto</Th>
              <Th color="white" fontSize="xs">Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {cargando ? (
              <Tr>
                <Td colSpan={8} textAlign="center" py={8}>
                  <Text color="gray.500">Cargando...</Text>
                </Td>
              </Tr>
            ) : datosFiltrados.length === 0 ? (
              <Tr>
                <Td colSpan={8} textAlign="center" py={8}>
                  <Text color="gray.400">No hay programas registrados para este plan</Text>
                </Td>
              </Tr>
            ) : (
              datosFiltrados.map((item, idx) => (
                <Tr key={item.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: 'red.50' }}>
                  <Td maxW="200px">
                    <Text noOfLines={1} title={item.nombrePrograma}>{item.nombrePrograma}</Text>
                  </Td>
                  <Td>{item.comunidad}</Td>
                  <Td>{item.tipoProgramaCultural}</Td>
                  <Td>{item.responsable}</Td>
                  <Td>{fmt(item.fechaInicio)}</Td>
                  <Td>{fmt(item.fechaFin)}</Td>
                  <Td isNumeric>{fmtPesos(item.presupuestoEstimado)}</Td>
                  <Td>
                    <HStack spacing={1}>
                      <Button size="xs" colorScheme="blue" variant="outline" onClick={() => handleVer(item)}>
                        Ver
                      </Button>
                      <Button size="xs" colorScheme="orange" variant="outline" onClick={() => handleEditar(item)}>
                        Editar
                      </Button>
                      <Button size="xs" colorScheme="red" variant="outline" onClick={() => handleEliminarClick(item)}>
                        Eliminar
                      </Button>
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Modal: Ver */}
      <Modal isOpen={isOpenVer} onClose={onCloseVer} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">
            Detalle del Programa
          </ModalHeader>
          <ModalBody py={5}>
            {selectedItem && (
              <FormularioPrograma modo="view" idPlan={idPlan} data={selectedItem} />
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onCloseVer}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal: Editar */}
      <Modal isOpen={isOpenEditar} onClose={onCloseEditar} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">
            Editar Programa
          </ModalHeader>
          <ModalBody py={5}>
            {selectedItem && (
              <FormularioPrograma
                modo="edit"
                idPlan={idPlan}
                data={selectedItem}
                onGuardado={handleGuardado}
                onCancelar={onCloseEditar}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Modal: Nuevo */}
      <Modal isOpen={isOpenNuevo} onClose={onCloseNuevo} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">
            Nuevo Programa Cultural
          </ModalHeader>
          <ModalBody py={5}>
            <FormularioPrograma
              modo="new"
              idPlan={idPlan}
              onGuardado={handleGuardado}
              onCancelar={onCloseNuevo}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* AlertDialog: Confirmar eliminación */}
      <AlertDialog isOpen={isOpenEliminar} leastDestructiveRef={cancelRef} onClose={onCloseEliminar}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color="#5b182b">
              Eliminar programa
            </AlertDialogHeader>
            <AlertDialogBody>
              ¿Estás seguro de que deseas eliminar el programa{' '}
              <strong>{selectedItem?.nombrePrograma}</strong>? Esta acción no se puede deshacer.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onCloseEliminar} isDisabled={eliminando}>
                Cancelar
              </Button>
              <Button
                colorScheme="red"
                onClick={handleEliminarConfirmar}
                ml={3}
                isLoading={eliminando}
                loadingText="Eliminando..."
              >
                Eliminar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default TablaProgramasCulturales;
